import './styles/main.css';
import { initAuth, signOut } from './services/auth.service.js';
import { getGroup } from './services/group.service.js';
import { store } from './store/app.store.js';
import { initRouter, navigate } from './router.js';
import { renderLoginPage } from './components/auth/LoginPage.js';
import { renderGroupList } from './components/groups/GroupList.js';
import { renderExpenseList } from './components/expenses/ExpenseList.js';
import { renderProfilePage } from './components/profile/ProfilePage.js';
import { renderJoinPage } from './components/join/JoinPage.js';
import type { Route } from './types/index.js';

const PENDING_JOIN_KEY = 'expdist:pendingJoinToken';

const appEl = document.getElementById('app')!;
let cleanupRoute: (() => void) | null = null;

// ── Inactivity auto-logout ──────────────────────────────────────────────────
const INACTIVITY_MS = 10 * 60 * 1000; // 10 minutes
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

function resetInactivityTimer() {
  if (!store.getState().user) return;
  if (inactivityTimer) clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(() => { signOut(); }, INACTIVITY_MS);
}

function startInactivityTracking() {
  const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
  events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
  resetInactivityTimer();
}

function stopInactivityTracking() {
  if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
}
// ───────────────────────────────────────────────────────────────────────────

function handleRoute(route: Route): void {
  const { user } = store.getState();

  // Auth guard
  if (!user && route.name !== 'login') {
    if (route.name === 'join') {
      sessionStorage.setItem(PENDING_JOIN_KEY, route.token);
    }
    navigate({ name: 'login' });
    return;
  }
  if (user && route.name === 'login') {
    navigate({ name: 'groups' });
    return;
  }

  // Tear down previous route
  cleanupRoute?.();
  cleanupRoute = null;
  appEl.innerHTML = '';

  switch (route.name) {
    case 'login':
      renderLoginPage(appEl);
      break;

    case 'groups':
      cleanupRoute = renderGroupList(appEl);
      break;

    case 'group':
      (async () => {
        appEl.innerHTML = '<div class="spinner spinner-center"></div>';
        const group = await getGroup(route.id);
        if (!group) {
          appEl.innerHTML = '<div class="error-page"><p>Group not found.</p></div>';
          return;
        }
        cleanupRoute = renderExpenseList(appEl, group);
      })();
      break;

    case 'profile':
      renderProfilePage(appEl);
      break;

    case 'join':
      cleanupRoute = renderJoinPage(appEl, route.token) ?? null;
      break;

    default:
      appEl.innerHTML = '<div class="error-page"><h2>404 — Page not found</h2></div>';
  }
}

// Wait for Firebase Auth to resolve before rendering
initAuth(() => {
  store.subscribe(() => {
    // Re-evaluate the current route whenever auth state changes
    const hash = window.location.hash;
    const { user } = store.getState();

    if (user) {
      startInactivityTracking();
    } else {
      stopInactivityTracking();
    }

    if (!user && hash !== '#/login' && !hash.startsWith('#/join/')) {
      navigate({ name: 'login' });
    } else if (user && (hash === '#/login' || hash === '' || hash === '#/')) {
      const pendingToken = sessionStorage.getItem(PENDING_JOIN_KEY);
      if (pendingToken) {
        sessionStorage.removeItem(PENDING_JOIN_KEY);
        navigate({ name: 'join', token: pendingToken });
      } else {
        navigate({ name: 'groups' });
      }
    }
  });

  initRouter(handleRoute);
});
